"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useMemo } from "react";
import { SectionHeader } from "fansunited-sports-ui";
import type { MarketOddsMap, MarketType, BookmakerOdds } from "@/features/odds/types";
import { MarketCard } from "@/features/odds/components/MarketCard";
import { collectMarketsByLine } from "@/features/odds/marketGrouping";
import { translateOddsSelections } from "@/features/odds/translateOddsLabels";
import { useLabels, useLocale } from "@/lib/labels/LabelsProvider";
import { useMatchPageData } from "../context/MatchPageDataContext";
interface OddsTabProps {
  homeTeamName: string;
  awayTeamName: string;
  kickoffTime: Date | string;
  venue?: string;
  /** Odds data passed from parent Match component (keyed by raw API market code). */
  oddsMap: MarketOddsMap;
  /** Loading state from parent */
  loading: boolean;
  /** Callback to manually refresh odds */
  onRefresh?: () => void;
  /** Whether the match is live */
  isLive?: boolean;
}

/** Map an Over/Under threshold to the BetslipMarket type (for betslip integration). */
function thresholdToOverGoalsMarket(threshold: number): MarketType | undefined {
  const map: Record<string, MarketType> = {
    "0.5": "OVER_GOALS_0_5",
    "1.5": "OVER_GOALS_1_5",
    "2.5": "OVER_GOALS_2_5",
    "3.5": "OVER_GOALS_3_5",
    "4.5": "OVER_GOALS_4_5",
    "5.5": "OVER_GOALS_5_5"
  };
  return map[threshold.toString()];
}

/** Format a signed handicap line: 0 → "0", 0.5 → "+0.5", -1.5 → "-1.5". */
function formatSignedLine(line: number): string {
  if (line === 0) return "0";
  return line > 0 ? `+${line}` : line.toString();
}

/**
 * OddsTab — one headered section per market type. Multi-line families
 * (`OVER_GOALS_*`, `HT_OVER_GOALS_*`, `ASIAN_HANDICAP_*`, `OVER_CORNERS_*`)
 * render one card per line inside their section. The odds column is short
 * ("Over" / "Under" / "1" / "X" / "2") because the card title already carries
 * the line ("Over/Under 2.5", "Asian Handicap +0.5", …).
 *
 * Section order mirrors what the industry does: primary result first, then
 * variants (Double Chance, DNB), then time-split (HT/FT, HT 1X2), then
 * scoring-shape markets (BTTS), then multi-line families, then specialty
 * markets (First Team to Score, Correct Score) last.
 *
 * The oddsMap is keyed by raw Sportal365 market code — see `useMatchOdds`
 * and `types.MarketOddsMap`.
 */
export const OddsTab: React.FC<OddsTabProps> = t0 => {
  const $ = _c(147);
  const {
    homeTeamName,
    awayTeamName,
    kickoffTime,
    venue,
    oddsMap,
    loading
  } = t0;
  const labels = useLabels();
  const locale = useLocale();
  const oddsLabels = labels.matchPage.odds;
  const {
    match,
    homeTeam,
    awayTeam
  } = useMatchPageData();
  const matchId = match.fansUnitedMatchId || match.id;
  let t1;
  if ($[0] !== kickoffTime) {
    t1 = new Date(kickoffTime);
    $[0] = kickoffTime;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const kickoffDate = t1;
  let t2;
  if ($[2] !== kickoffTime) {
    t2 = typeof kickoffTime === "string" ? kickoffTime : kickoffTime instanceof Date ? kickoffTime.toISOString() : undefined;
    $[2] = kickoffTime;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const kickoffAtStr = t2;
  let t3;
  if ($[4] !== kickoffDate || $[5] !== locale) {
    t3 = kickoffDate.toLocaleDateString(locale, {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric"
    });
    $[4] = kickoffDate;
    $[5] = locale;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const formattedDate = t3;
  let t4;
  if ($[7] !== kickoffDate || $[8] !== locale) {
    t4 = kickoffDate.toLocaleTimeString(locale, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    });
    $[7] = kickoffDate;
    $[8] = locale;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const formattedTime = t4;
  const subtitle = venue ? `${formattedDate} • ${formattedTime} • ${venue}` : `${formattedDate} • ${formattedTime}`;
  let t5;
  if ($[10] !== oddsMap) {
    t5 = oddsMap.get("FT_1X2") ?? [];
    $[10] = oddsMap;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const matchResultOdds = t5;
  let t6;
  if ($[12] !== oddsMap) {
    t6 = oddsMap.get("DOUBLE_CHANCE") ?? [];
    $[12] = oddsMap;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const doubleChanceOdds = t6;
  let t7;
  if ($[14] !== oddsMap) {
    t7 = oddsMap.get("DRAW_NO_BET") ?? [];
    $[14] = oddsMap;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const drawNoBetOdds = t7;
  let t8;
  if ($[16] !== oddsMap) {
    t8 = oddsMap.get("HT_FT") ?? [];
    $[16] = oddsMap;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const htftOdds = t8;
  let t9;
  if ($[18] !== oddsMap) {
    t9 = oddsMap.get("HT_1X2") ?? [];
    $[18] = oddsMap;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const ht1x2Odds = t9;
  let t10;
  if ($[20] !== oddsMap) {
    t10 = oddsMap.get("BOTH_TEAMS_SCORE") ?? [];
    $[20] = oddsMap;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const bttsOdds = t10;
  let t11;
  if ($[22] !== oddsMap) {
    t11 = oddsMap.get("CORRECT_SCORE") ?? [];
    $[22] = oddsMap;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const correctScoreOdds = t11;
  let t12;
  if ($[24] !== oddsMap) {
    t12 = oddsMap.get("TEAM_SCORE_FIRST_GOAL") ?? [];
    $[24] = oddsMap;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const firstTeamToScoreOdds = t12;
  let t13;
  if ($[26] !== oddsMap) {
    t13 = collectMarketsByLine(oddsMap, "OVER_GOALS_");
    $[26] = oddsMap;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const overUnderByLine = t13;
  let t14;
  if ($[28] !== oddsMap) {
    t14 = collectMarketsByLine(oddsMap, "HT_OVER_GOALS_");
    $[28] = oddsMap;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const htOverUnderByLine = t14;
  let t15;
  if ($[30] !== oddsMap) {
    t15 = collectMarketsByLine(oddsMap, "ASIAN_HANDICAP_");
    $[30] = oddsMap;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  const asianHandicapByLine = t15;
  let t16;
  if ($[32] !== oddsMap) {
    t16 = collectMarketsByLine(oddsMap, "OVER_CORNERS_");
    $[32] = oddsMap;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  const cornersByLine = t16;
  let t17;
  if ($[34] !== doubleChanceOdds || $[35] !== oddsLabels) {
    t17 = translateOddsSelections(doubleChanceOdds, "DOUBLE_CHANCE", oddsLabels);
    $[34] = doubleChanceOdds;
    $[35] = oddsLabels;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  const translatedDoubleChanceOdds = t17;
  let t18;
  if ($[37] !== awayTeam.name || $[38] !== homeTeam.name || $[39] !== htftOdds || $[40] !== labels.predictorPage.odds.draw || $[41] !== oddsLabels) {
    t18 = translateOddsSelections(htftOdds, "FIRST_HALF_AND_FINAL_RESULT", oddsLabels, {
      homeTeamName: homeTeam.name,
      awayTeamName: awayTeam.name,
      drawLabel: labels.predictorPage.odds.draw
    });
    $[37] = awayTeam.name;
    $[38] = homeTeam.name;
    $[39] = htftOdds;
    $[40] = labels.predictorPage.odds.draw;
    $[41] = oddsLabels;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  const translatedHtftOdds = t18;
  let t19;
  if ($[43] !== bttsOdds || $[44] !== oddsLabels) {
    t19 = translateOddsSelections(bttsOdds, "BOTH_TEAMS_SCORE", oddsLabels);
    $[43] = bttsOdds;
    $[44] = oddsLabels;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  const translatedBttsOdds = t19;
  let t20;
  if ($[46] !== oddsLabels) {
    t20 = bookies => translateOddsSelections(bookies, "OVER_UNDER", oddsLabels);
    $[46] = oddsLabels;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  const translateOverUnder = t20;
  let t21;
  if ($[48] !== matchResultOdds || $[49] !== oddsLabels) {
    t21 = translateOddsSelections(matchResultOdds, "FT_1X2", oddsLabels);
    $[48] = matchResultOdds;
    $[49] = oddsLabels;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  const translated1X2Odds = t21;
  let t22;
  if ($[51] !== ht1x2Odds || $[52] !== oddsLabels) {
    t22 = translateOddsSelections(ht1x2Odds, "HT_1X2", oddsLabels);
    $[51] = ht1x2Odds;
    $[52] = oddsLabels;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  const translatedHt1X2Odds = t22;
  const hasAnyOdds = oddsMap.size > 0;
  let t23;
  if ($[54] !== awayTeamName || $[55] !== homeTeamName || $[56] !== labels.common.vs) {
    t23 = <h1 className="text-2xl md:text-4xl font-bold text-foreground">{homeTeamName} {labels.common.vs} {awayTeamName}</h1>;
    $[54] = awayTeamName;
    $[55] = homeTeamName;
    $[56] = labels.common.vs;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  let t24;
  if ($[58] !== subtitle) {
    t24 = <p className="text-muted-foreground mt-1">{subtitle}</p>;
    $[58] = subtitle;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] !== t23 || $[61] !== t24) {
    t25 = <div>{t23}{t24}</div>;
    $[60] = t23;
    $[61] = t24;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  let t26;
  if ($[63] !== loading) {
    t26 = loading && <div className="animate-pulse space-y-6"><div className="h-6 bg-muted rounded w-32 mb-4" /><div className="grid grid-cols-1 md:grid-cols-2 gap-4"><div className="bg-card rounded-lg border border-border p-4 h-24" /><div className="bg-card rounded-lg border border-border p-4 h-24" /></div></div>;
    $[63] = loading;
    $[64] = t26;
  } else {
    t26 = $[64];
  }
  let t27;
  if ($[65] !== doubleChanceOdds || $[66] !== drawNoBetOdds || $[67] !== kickoffAtStr || $[68] !== loading || $[69] !== matchId || $[70] !== matchResultOdds || $[71] !== oddsLabels || $[72] !== translated1X2Odds || $[73] !== translatedDoubleChanceOdds) {
    t27 = !loading && (matchResultOdds.length > 0 || doubleChanceOdds.length > 0 || drawNoBetOdds.length > 0) && <Section title={oddsLabels.markets.matchResult}><Grid>{matchResultOdds.length > 0 && <MarketCard title={oddsLabels.markets.whoWillWin} bookmakerOdds={translated1X2Odds} matchId={matchId} market="FT_1X2" kickoffAt={kickoffAtStr} />}{doubleChanceOdds.length > 0 && <MarketCard title={oddsLabels.markets.doubleChance} bookmakerOdds={translatedDoubleChanceOdds} matchId={matchId} market="DOUBLE_CHANCE" kickoffAt={kickoffAtStr} />}{drawNoBetOdds.length > 0 && <MarketCard title={oddsLabels.markets.drawNoBet} bookmakerOdds={drawNoBetOdds} matchId={matchId} kickoffAt={kickoffAtStr} />}</Grid></Section>;
    $[65] = doubleChanceOdds;
    $[66] = drawNoBetOdds;
    $[67] = kickoffAtStr;
    $[68] = loading;
    $[69] = matchId;
    $[70] = matchResultOdds;
    $[71] = oddsLabels;
    $[72] = translated1X2Odds;
    $[73] = translatedDoubleChanceOdds;
    $[74] = t27;
  } else {
    t27 = $[74];
  }
  let t28;
  if ($[75] !== htftOdds || $[76] !== kickoffAtStr || $[77] !== loading || $[78] !== matchId || $[79] !== oddsLabels || $[80] !== translatedHtftOdds) {
    t28 = !loading && htftOdds.length > 0 && <Section title={oddsLabels.markets.halfTimeFullTime}><MarketCard title={oddsLabels.predictHtFt} bookmakerOdds={translatedHtftOdds} matchId={matchId} market="HT_FT" kickoffAt={kickoffAtStr} /></Section>;
    $[75] = htftOdds;
    $[76] = kickoffAtStr;
    $[77] = loading;
    $[78] = matchId;
    $[79] = oddsLabels;
    $[80] = translatedHtftOdds;
    $[81] = t28;
  } else {
    t28 = $[81];
  }
  let t29;
  if ($[82] !== ht1x2Odds || $[83] !== htOverUnderByLine || $[84] !== kickoffAtStr || $[85] !== loading || $[86] !== matchId || $[87] !== oddsLabels || $[88] !== translateOverUnder || $[89] !== translatedHt1X2Odds) {
    t29 = !loading && (ht1x2Odds.length > 0 || htOverUnderByLine.size > 0) && <Section title={oddsLabels.markets.halfTimeMarkets}><Grid>{ht1x2Odds.length > 0 && <MarketCard title={oddsLabels.markets.halfTime1x2} bookmakerOdds={translatedHt1X2Odds} matchId={matchId} kickoffAt={kickoffAtStr} />}{[...htOverUnderByLine.entries()].map(t30 => {
          const [line, bookies_0] = t30;
          return <MarketCard key={`ht-ou-${line}`} title={`${oddsLabels.selectionsOverUnder.over}/${oddsLabels.selectionsOverUnder.under} ${line}`} bookmakerOdds={translateOverUnder(bookies_0)} matchId={matchId} kickoffAt={kickoffAtStr} />;
        })}</Grid></Section>;
    $[82] = ht1x2Odds;
    $[83] = htOverUnderByLine;
    $[84] = kickoffAtStr;
    $[85] = loading;
    $[86] = matchId;
    $[87] = oddsLabels;
    $[88] = translateOverUnder;
    $[89] = translatedHt1X2Odds;
    $[90] = t29;
  } else {
    t29 = $[90];
  }
  let t30;
  if ($[91] !== bttsOdds || $[92] !== kickoffAtStr || $[93] !== loading || $[94] !== matchId || $[95] !== oddsLabels || $[96] !== translatedBttsOdds) {
    t30 = !loading && bttsOdds.length > 0 && <Section title={oddsLabels.markets.bothTeamsToScore}><MarketCard title={oddsLabels.willBothTeamsScore} bookmakerOdds={translatedBttsOdds} matchId={matchId} market="BOTH_TEAMS_SCORE" kickoffAt={kickoffAtStr} /></Section>;
    $[91] = bttsOdds;
    $[92] = kickoffAtStr;
    $[93] = loading;
    $[94] = matchId;
    $[95] = oddsLabels;
    $[96] = translatedBttsOdds;
    $[97] = t30;
  } else {
    t30 = $[97];
  }
  let t31;
  if ($[98] !== kickoffAtStr || $[99] !== loading || $[100] !== matchId || $[101] !== oddsLabels || $[102] !== overUnderByLine || $[103] !== translateOverUnder) {
    t31 = !loading && overUnderByLine.size > 0 && <Section title={oddsLabels.markets.overUnderGoals}><Grid>{[...overUnderByLine.entries()].map(t32 => {
          const [line_0, bookies_1] = t32;
          return <MarketCard key={`ou-${line_0}`} title={`${oddsLabels.selectionsOverUnder.over}/${oddsLabels.selectionsOverUnder.under} ${line_0}`} bookmakerOdds={translateOverUnder(bookies_1)} matchId={matchId} market={thresholdToOverGoalsMarket(line_0)} kickoffAt={kickoffAtStr} />;
        })}</Grid></Section>;
    $[98] = kickoffAtStr;
    $[99] = loading;
    $[100] = matchId;
    $[101] = oddsLabels;
    $[102] = overUnderByLine;
    $[103] = translateOverUnder;
    $[104] = t31;
  } else {
    t31 = $[104];
  }
  let t32;
  if ($[105] !== asianHandicapByLine || $[106] !== kickoffAtStr || $[107] !== loading || $[108] !== matchId || $[109] !== oddsLabels) {
    t32 = !loading && asianHandicapByLine.size > 0 && <Section title={oddsLabels.markets.asianHandicap}><Grid>{[...asianHandicapByLine.entries()].map(t33 => {
          const [line_1, bookies_2] = t33;
          return <MarketCard key={`ah-${line_1}`} title={`${oddsLabels.markets.asianHandicap} ${formatSignedLine(line_1)}`} bookmakerOdds={bookies_2} matchId={matchId} kickoffAt={kickoffAtStr} />;
        })}</Grid></Section>;
    $[105] = asianHandicapByLine;
    $[106] = kickoffAtStr;
    $[107] = loading;
    $[108] = matchId;
    $[109] = oddsLabels;
    $[110] = t32;
  } else {
    t32 = $[110];
  }
  let t33;
  if ($[111] !== firstTeamToScoreOdds || $[112] !== kickoffAtStr || $[113] !== loading || $[114] !== matchId || $[115] !== oddsLabels) {
    t33 = !loading && firstTeamToScoreOdds.length > 0 && <Section title={oddsLabels.markets.firstTeamToScore}><MarketCard title={oddsLabels.markets.firstTeamToScore} bookmakerOdds={firstTeamToScoreOdds} matchId={matchId} kickoffAt={kickoffAtStr} /></Section>;
    $[111] = firstTeamToScoreOdds;
    $[112] = kickoffAtStr;
    $[113] = loading;
    $[114] = matchId;
    $[115] = oddsLabels;
    $[116] = t33;
  } else {
    t33 = $[116];
  }
  let t34;
  if ($[117] !== cornersByLine || $[118] !== kickoffAtStr || $[119] !== loading || $[120] !== matchId || $[121] !== oddsLabels || $[122] !== translateOverUnder) {
    t34 = !loading && cornersByLine.size > 0 && <Section title={oddsLabels.markets.corners}><Grid>{[...cornersByLine.entries()].map(t35 => {
          const [line_2, bookies_3] = t35;
          return <MarketCard key={`corners-${line_2}`} title={`${oddsLabels.selectionsOverUnder.over}/${oddsLabels.selectionsOverUnder.under} ${line_2}`} bookmakerOdds={translateOverUnder(bookies_3)} matchId={matchId} kickoffAt={kickoffAtStr} />;
        })}</Grid></Section>;
    $[117] = cornersByLine;
    $[118] = kickoffAtStr;
    $[119] = loading;
    $[120] = matchId;
    $[121] = oddsLabels;
    $[122] = translateOverUnder;
    $[123] = t34;
  } else {
    t34 = $[123];
  }
  let t35;
  if ($[124] !== correctScoreOdds || $[125] !== kickoffAtStr || $[126] !== loading || $[127] !== matchId || $[128] !== oddsLabels) {
    t35 = !loading && correctScoreOdds.length > 0 && <Section title={oddsLabels.markets.correctScore}><MarketCard title={oddsLabels.predictExactScore} bookmakerOdds={correctScoreOdds} matchId={matchId} market="CORRECT_SCORE" kickoffAt={kickoffAtStr} /></Section>;
    $[124] = correctScoreOdds;
    $[125] = kickoffAtStr;
    $[126] = loading;
    $[127] = matchId;
    $[128] = oddsLabels;
    $[129] = t35;
  } else {
    t35 = $[129];
  }
  let t36;
  if ($[130] !== hasAnyOdds || $[131] !== loading || $[132] !== oddsLabels) {
    t36 = !loading && !hasAnyOdds && <div className="bg-card rounded-lg shadow-md border border-border p-6 text-center"><p className="text-muted-foreground">{oddsLabels.noOddsAvailable}</p></div>;
    $[130] = hasAnyOdds;
    $[131] = loading;
    $[132] = oddsLabels;
    $[133] = t36;
  } else {
    t36 = $[133];
  }
  let t37;
  if ($[134] !== t25 || $[135] !== t26 || $[136] !== t27 || $[137] !== t28 || $[138] !== t29 || $[139] !== t30 || $[140] !== t31 || $[141] !== t32 || $[142] !== t33 || $[143] !== t34 || $[144] !== t35 || $[145] !== t36) {
    t37 = <div className="space-y-6">{t25}{t26}{t27}{t28}{t29}{t30}{t31}{t32}{t33}{t34}{t35}{t36}</div>;
    $[134] = t25;
    $[135] = t26;
    $[136] = t27;
    $[137] = t28;
    $[138] = t29;
    $[139] = t30;
    $[140] = t31;
    $[141] = t32;
    $[142] = t33;
    $[143] = t34;
    $[144] = t35;
    $[145] = t36;
    $[146] = t37;
  } else {
    t37 = $[146];
  }
  return t37;
};
function Section(t0) {
  const $ = _c(9);
  const {
    title,
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      typography: {
        name: "text-sm uppercase font-semibold text-foreground"
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== title) {
    t2 = {
      name: title,
      styles: t1
    };
    $[1] = title;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      container: "mb-3"
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2) {
    t4 = <SectionHeader entity={t2} styles={t3} />;
    $[4] = t2;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== children || $[7] !== t4) {
    t5 = <div>{t4}{children}</div>;
    $[6] = children;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}
function Grid(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}